/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { EventAction, EventCategory } from "constants/tracking"
import {
  AUTHN_TOKEN_JTI_COOKIE,
  isAuthnTokenJtiPresent,
  readAuthnTokenJti,
  readReadableCookies,
} from "./authnCookie"
import { sleep } from "./misc"
import { SessionErrorReason } from "./sessionError"
import { TroyWebTracker } from "./tracking"
import { getMexAppInfo, isWebview } from "./webview"

/**
 * The readable half of the cookie pair the native app injects for webview
 * cookie auth: `mexusers_authn_token` is HttpOnly (JS can never see it), its
 * jti twin is not. Since the two are injected together, the jti's PRESENCE is
 * the best available proxy for whether the token cookie arrived — the same
 * probe mex-mobile-web runs.
 *
 * The jti VALUE is reported too, as the runbook's join key to the backend's
 * record of the same token. What is never reported is the HttpOnly twin
 * `mexusers_authn_token` — the credential itself, which JS cannot read here at
 * all. See `readAuthnTokenJti` for why the value is not as inert as the runbook
 * assumes, and the decision behind logging it anyway.
 */
export { AUTHN_TOKEN_JTI_COOKIE }

export interface AuthnTokenJtiAvailability {
  /**
   * The jti's value — the join key to the backend's record of the same token,
   * which the WebView Auth Migration runbook asks for by name. Opaque and
   * unable to authenticate; its HttpOnly twin `mexusers_authn_token`, which
   * can, is unreadable here and is never logged.
   *
   * Scribe drops empty-string facets, so an absent jti makes this field vanish
   * rather than arrive as "". AUTHN_JTI_PRESENT is what carries that meaning.
   */
  AUTHN_TOKEN_JTI: string
  AUTHN_JTI_PRESENT: boolean
  READABLE_COOKIE_COUNT: number
}

/**
 * READABLE_COOKIE_COUNT guards the reading of AUTHN_JTI_PRESENT=false:
 * `document.cookie` only exposes non-HttpOnly cookies, so if it sees nothing at
 * all (cookies blocked, storage partitioned) an absent jti proves nothing,
 * whereas absent-with-other-cookies-readable means injection did not happen.
 */
export const readAuthnTokenJtiAvailability = (): AuthnTokenJtiAvailability => {
  // Same probe the auth path runs (see `utils/authnCookie`), so this telemetry
  // reports what `isCookieAuthEnabled` actually decided on rather than a second
  // opinion that could disagree with it.
  const cookies = readReadableCookies()

  return {
    AUTHN_TOKEN_JTI: readAuthnTokenJti(cookies),
    AUTHN_JTI_PRESENT: isAuthnTokenJtiPresent(cookies),
    READABLE_COOKIE_COUNT: cookies.length,
  }
}

/**
 * One Scribe record per webview page load of the app/device context that no
 * per-interaction event carries (BP-55471): native platform and build from the
 * UA token (app version and OS version already ride on every event as default
 * parameters), whether the app's auth cookie pair was injected (see
 * readAuthnTokenJtiAvailability), and the device geometry the mobile layout
 * actually rendered at. No-op outside the app webview.
 *
 * "Per page load" is enforced by WHERE this is called, so keep it there: the
 * mount effect of `Home`, whose Route is pathless and therefore matches every
 * route. Calling it from a route-scoped component instead (it was originally in
 * NavigationContainer) emits a second event whenever navigation leaves that
 * component's paths and returns, and none at all on a load that starts outside
 * them.
 *
 * Every field is coerced to a concrete value: normalizedFacets drops
 * null/undefined at send, which would make "absent" indistinguishable from
 * "failed to read".
 */
/**
 * The native build these labels describe, coerced to concrete values because
 * normalizedFacets drops null/undefined at send — which would make "absent"
 * indistinguishable from "failed to read".
 *
 * OS_VERSION and MEX_APP_VERSION are deliberately NOT here: they ride on every
 * event as default parameters (getDefaultParameters in tracking.ts), which are
 * spread AFTER labels and would overwrite them anyway.
 */
const readMexAppLabels = (): Record<string, string> => {
  const appInfo = getMexAppInfo()
  return {
    MEX_APP_PLATFORM: appInfo?.platform ?? "",
    MEX_APP_BUILD: appInfo?.appBuild ?? "",
  }
}

export const sendWebviewSessionEvent = (): void => {
  if (!isWebview()) {
    return
  }

  TroyWebTracker.sendEvent({
    category: EventCategory.webview,
    action: EventAction.webviewSessionStart,
    labels: {
      ...readMexAppLabels(),
      ...readAuthnTokenJtiAvailability(),
      COOKIES_ENABLED: window.navigator.cookieEnabled === true,
      LANGUAGE: window.navigator.language ?? "",
      SCREEN_WIDTH: window.screen?.width ?? 0,
      SCREEN_HEIGHT: window.screen?.height ?? 0,
      VIEWPORT_WIDTH: window.innerWidth ?? 0,
      VIEWPORT_HEIGHT: window.innerHeight ?? 0,
      DEVICE_PIXEL_RATIO: window.devicePixelRatio ?? 0,
    },
  })
}

/**
 * One Scribe record when the webview session dies and the merchant is shown the
 * retryable error screen (BP-55578). The same moment is reported to Datadog for
 * alerting; this is the funnel half — WEBVIEW_SESSION_START is the denominator,
 * so the two together give a rate rather than a raw count.
 *
 * `REASON` separates a token rejected mid-session from the app handing over an
 * incomplete one, which have different owners. The jti probe is repeated here
 * on purpose: read at the moment of failure it says whether the auth cookies
 * were present when the session broke, which the page-load reading cannot.
 *
 * Emitted once per page load, because `emitSessionError` latches. No-op outside
 * the app webview.
 */
export const sendWebviewSessionErrorEvent = (
  reason: SessionErrorReason,
): void => {
  if (!isWebview()) {
    return
  }

  TroyWebTracker.sendEvent({
    category: EventCategory.webview,
    action: EventAction.webviewSessionError,
    labels: {
      REASON: reason,
      ...readAuthnTokenJtiAvailability(),
    },
  })
}

/**
 * How a Retry on the session-error screen ended (BP-57525).
 *
 * - `noBridge` — no JS bridge at all: a desktop browser, or the `?webview=true`
 *   override. The page reloaded instead; nothing was asked of the app.
 * - `success` — the app CONFIRMED the refresh, so the cookies are fresh.
 * - `noAnswer` — the app resolved without confirming anything: `null`, an empty
 *   object, an undocumented shape. This is what an app build that never
 *   registered the handler returns, so it is the outcome that measures a
 *   partial app rollout — which is why it is not folded into `success`, even
 *   though the merchant's page reloads identically.
 * - `failed` — the app said so. It tried and could not renew.
 * - `rejected` — the call threw on the app side. NOT an unregistered handler:
 *   `flutter_inappwebview` resolves those rather than rejecting, so they land
 *   in `noAnswer`.
 * - `timeout` — the app never settled the call and the watchdog reloaded.
 */
export type SessionRetryOutcome =
  | "noBridge"
  | "noAnswer"
  | "success"
  | "failed"
  | "rejected"
  | "timeout"

/**
 * How long the reload will wait for the queued event to reach Scribe.
 *
 * The event is emitted one line before `window.location.reload()`, and the SDK
 * only queues: it debounces the POST by 5s (`inactiveTimeout` in its
 * `rate_limiter.js`), and its own `beforeunload` flush uses `keepalive: false`,
 * which the browser cancels as the page goes away. So this event has to be
 * pushed out and waited on, or it never arrives.
 *
 * Capped because the merchant's exit outranks the measurement: a slow or dead
 * Scribe delays the reload by at most this long.
 */
const RETRY_FLUSH_TIMEOUT_MS = 1000

/**
 * One Scribe record per Retry press on the session-error screen (BP-57525).
 *
 * WEBVIEW_SESSION_ERROR says how often the webview loses a session;
 * this says how often the merchant gets it back, which is the only way to tell
 * a working `refreshAuthSession` from one the app never registered. Sliced by
 * build, because that is the axis a partial app rollout moves along.
 *
 * Not latched: unlike the error event, Retry is a deliberate press and pressing
 * it twice is itself the signal.
 *
 * Awaitable, and the caller MUST await it: this is the one event in the file
 * emitted immediately before a navigation, so it is flushed rather than left in
 * the SDK's queue. See RETRY_FLUSH_TIMEOUT_MS.
 *
 * No-op outside the app webview.
 */
export const sendWebviewSessionRetryEvent = async (
  outcome: SessionRetryOutcome,
  error?: string,
): Promise<void> => {
  if (!isWebview()) {
    return
  }

  TroyWebTracker.sendEvent({
    category: EventCategory.webview,
    action: EventAction.webviewSessionRetry,
    labels: {
      OUTCOME: outcome,
      // The app's own failure code, and only that — never a cookie, token or
      // anything read off the session it failed to renew.
      ERROR: error ?? "",
      ...readMexAppLabels(),
    },
  })

  await Promise.race([TroyWebTracker.flush(), sleep(RETRY_FLUSH_TIMEOUT_MS)])
}

/**
 * One Scribe record when the app rejects the bottom-nav visibility call
 * (BP-55782). The merchant is left with a bar that contradicts the screen — an
 * outcome nothing on the web side can correct, and one they are unlikely to
 * report, so this is the only way to see it happening.
 *
 * `HIDDEN` is the state we FAILED to apply, not the state the bar is in: it
 * separates "the full-page screen still has a bar under it" from "the list lost
 * its bar", which look nothing alike to the merchant.
 *
 * A rejection cannot distinguish an app build with no handler under this name
 * from a handler that ran and threw — both reject identically — so treat a
 * cluster of these on one app version as the former and a scatter as the
 * latter. Latched by the caller to once per page load: the call fires on every
 * route change, and an app build without the handler would otherwise emit one
 * record per navigation and drown the signal in its own volume.
 *
 * No-op outside the app webview.
 */
export const sendWebviewBottomNavErrorEvent = (
  hidden: boolean,
  source?: string,
): void => {
  if (!isWebview()) {
    return
  }

  TroyWebTracker.sendEvent({
    category: EventCategory.webview,
    action: EventAction.webviewBottomNavError,
    labels: {
      HIDDEN: hidden,
      ...(source && { SOURCE: source }),
      // Repeated from WEBVIEW_SESSION_START rather than joined to it: whether
      // an app build registers this handler is the first thing to slice by, and
      // the platform/build pair is what names the build.
      ...readMexAppLabels(),
    },
  })
}
